/* Clinical Dashboard Overview
   Refactored per AbdulRafiu's confirmed structure:
     /dashboard        -> Clinical Dashboard Overview (this file)
     /dashboard/triage -> Triage Centre (src/pages/TriageCenter.jsx)
     /dashboard/roster -> Patient Roster (src/pages/PatientRoster.jsx)

   Everything triage-oriented that used to live here (AlertFilter,
   PatientList, selected-patient Vitals/Device panels, AI Risk/
   Explainability/Recommendation panels, ML Feedback loop) has moved to
   TriageCenter.jsx unchanged. This page is now a lightweight population
   summary + navigation surface, independent of whether anyone is on the
   Triage or Roster page.

   Added 02/09/2026: AlertSummaryWidget, per AbdulRafiu's Q3 decision —
   "The Clinical Dashboard should provide an alert summary/counts and
   navigation into Triage rather than unnecessarily duplicate the full
   operational alert workflow." Added as its own section; ClinicalOverviewCard
   and useClinicalOverviewStats are untouched.
*/
import React from "react";
import * as FiIcons from "react-icons/fi";
const { FiActivity } = FiIcons;
import { useAuth } from "../lib/auth";
import useClinicalOverviewStats from "../hooks/useClinicalOverviewStats";
import ClinicalOverviewCard from "../components/dashboard/ClinicalOverviewCard";
import AskIVivaCoreAI from "../components/dashboard/AskvivacoreReal";
import AlertSummaryWidget from "../components/AlertSummaryWidget";

const Dashboard = () => {
  const { user } = useAuth();
  const { stats, isLoading, error, refresh } = useClinicalOverviewStats();

  return (
    <div className="min-h-screen text-slate-200">
      <div className="max-w-[1600px] mx-auto p-4 lg:p-6">
        <header className="flex justify-between items-center mb-6 lg:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight">
              CLINICAL DASHBOARD OVERVIEW
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                Tenant: {user?.managed_tenants?.length || 0} Authorized Scopes
              </span>
            </div>
          </div>
        </header>

        <div className="space-y-6 lg:space-y-8">
          {error ? (
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4 flex items-center justify-between">
              <p className="text-sm text-rose-300">{error}</p>
              <button
                onClick={refresh}
                className="text-xs font-bold text-rose-300 border border-rose-500/30 px-3 py-1.5 rounded-lg hover:bg-rose-500/10"
              >
                Retry
              </button>
            </div>
          ) : (
            <ClinicalOverviewCard stats={stats} isLoading={isLoading} />
          )}

          <AlertSummaryWidget />
        </div>
      </div>

      <AskIVivaCoreAI role="clinician" />
    </div>
  );
};

export default Dashboard;